<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Verified Requests ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-row v-show="showSearch" align="center" style="max-width: 410px;">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search file owner, file name or file template"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearch = !showSearch">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>
      </template>
    </v-app-bar>

    <div
      class="mt-12 mx-4"
      id="user-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table>
        <thead>
          <tr>
            <th width="20%" colspan="2" class="text-left pa-0">File Owner</th>
            <th width="20%" class="text-left">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    File Name
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group v-model="sort" hide-details class="mt-0 py-0">
                    <div v-for="(item, key) in fileNameSort" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchRequests">
                          <v-radio :value="item.value" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== fileNameSort.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="15%" class="text-left">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    File Template
                    <v-icon right>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group
                    v-model="filter.template"
                    hide-details
                    class="mt-0 py-0"
                  >
                    <div v-for="(filter, key) in templatesFilters" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{
                          filter.label
                        }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchRequests">
                          <v-radio :value="key + 1" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== templatesFilters.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="20%" class="text-left">
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Date Requested
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-date-picker
                  v-model="selectedDate"
                  @change="filterDateRequests"
                ></v-date-picker>
              </v-menu>
            </th>
            <th width="10%" class="text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="request in requests" :key="request.id">
            <td colspan="2" class="text-left pl-0">
              <user-avatar
                :avatar="request.user.avatar"
                size="40"
                class="mr-12"
              />
              {{ request.user.full_name }}
            </td>
            <td>{{ request.name }}</td>
            <td>
              {{
                request.template
                  ? request.template.label
                  : 'Custom - No Template'
              }}
            </td>
            <td>{{ request.addedDate }}</td>
            <td>
              <v-chip class="ma-2" color="#D1FFEF">
                <span class="text-capitalize green--text">{{
                  request.status
                }}</span>
              </v-chip>
            </td>
          </tr>
          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="py-3 pl-0" width="5%">
                <v-skeleton-loader
                  class="avatar-loading--36"
                  type="avatar"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(requests.length > 0) && !loading"
      >
        <p>No verified requests.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiMenuDown
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import TemplatesMixin from '@/utils/mixins/Templates'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import UserAvatar from '@/components/UserAvatar'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'

export default {
  name: 'VerifiedRequestsPage',

  mixins: [ControlsMixin, TemplatesMixin],

  directives: {
    infiniteScroll
  },

  components: {
    UserAvatar,
    AppBarNavIcon
  },

  data() {
    return {
      loading: false,
      showSearch: true,
      selectedRequest: null,
      showRequestModal: false,
      sort: null,
      filter: {
        search: '',
        status: 'valid',
        template: null
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        menu: mdiMenuDown
      },
      dateSort: [
        { value: 'created_at', text: 'Ascending' },
        { value: '-created_at', text: 'Descending' }
      ],
      fileNameSort: [
        { value: 'name', text: 'Ascending' },
        { value: '-name', text: 'Descending' }
      ],
      datePicker: false,
      selectedDate: null
    }
  },

  created() {
    this.clearRequests()
    this.fetchRequests(1)
  },

  computed: {
    ...mapState({
      requests: state => state.request.list,
      listMeta: state => state.request.listMeta
    }),
    templatesFilters() {
      let templates = this.templates
      templates.push({ label: 'Custom - No Template' }, { label: 'All' })
      return templates
    }
  },

  methods: {
    ...mapActions({
      getRequests: 'request/getRequests'
    }),

    ...mapMutations({
      clearRequests: 'request/clearRequestList'
    }),

    pageChanged(page) {
      this.fetchRequests(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchRequests(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearRequests()
      this.fetchRequests()
    }, 600),

    async fetchRequests(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      if (this.sort) {
        params.sort = this.sort
      }
      if (this.selectedDate) {
        params.searchDate = this.selectedDate
      }
      if (this.filter.template) {
        const template = this.filter.template
        params.template =
          template < 11 ? template : template === 11 ? null : undefined
      }
      params.status = this.filter.status

      this.loading = true
      await this.getRequests(params)
      this.loading = false
    },

    updateFetchRequests() {
      this.clearRequests()
      this.fetchRequests()
    },

    filterDateRequests: debounce(function() {
      this.datePicker = false
      this.clearRequests()
      this.fetchRequests()
    }, 600),

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    }
  }
}
</script>
